import { useState } from "react";
import { Check, X, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// ─── Canonical subtask control ──────────────────────────────────────────────
// Identity is ALWAYS the stable subtask `id` — toggle / rename / remove target
// by id, NEVER by array index. Presentational: the parent owns the array and
// the persistence strategy. Used by BOTH the View Task surface (toggle-only,
// immediate persist) and the Edit Task drawer (buffered add/rename/remove).
export function SubtaskList({ items = [], onToggle, canEdit = false, onAdd, onRemove, onRename, pending, testid = "subtask" }) {
  const [draft, setDraft] = useState("");
  const done = items.filter((s) => s.done).length;
  const add = () => { const v = draft.trim(); if (!v) return; onAdd?.(v); setDraft(""); };
  const isPending = (id) => !!pending?.has?.(id);

  return (
    <div className="space-y-2" data-testid={`${testid}-list`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">Subtasks</span>
        <span className="text-sm font-medium text-muted-foreground" data-testid={`${testid}-count`}>{done} / {items.length}</span>
      </div>

      {items.length === 0 && !canEdit && <p className="text-sm text-muted-foreground/70">No subtasks.</p>}

      <div className="space-y-1">
        {items.map((s) => (
          <div key={s.id} data-testid={`${testid}-item-${s.id}`}
            className={cn("group flex items-center gap-3 rounded-lg py-1", canEdit && "border border-border/60 bg-background px-2.5 py-1.5")}>
            <button type="button" disabled={isPending(s.id)} data-testid={`${testid}-toggle-${s.id}`}
              aria-label={s.done ? "Mark subtask incomplete" : "Mark subtask complete"}
              onClick={() => onToggle?.(s.id)}
              className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors disabled:opacity-50",
                s.done ? "border-success bg-success text-white" : "border-border hover:border-accent")}>
              {s.done && <Check className="h-3.5 w-3.5" />}
            </button>
            {canEdit ? (
              <input value={s.title} onChange={(e) => onRename?.(s.id, e.target.value)} data-testid={`${testid}-title-${s.id}`}
                className={cn("min-w-0 flex-1 bg-transparent text-[15px] focus:outline-none", s.done && "text-muted-foreground line-through")} />
            ) : (
              <span data-testid={`${testid}-title-${s.id}`}
                className={cn("min-w-0 flex-1 text-[15px] leading-relaxed", s.done ? "text-muted-foreground line-through" : "text-foreground/90")}>{s.title}</span>
            )}
            {canEdit && (
              <button type="button" onClick={() => onRemove?.(s.id)} data-testid={`${testid}-remove-${s.id}`}
                className="shrink-0 text-muted-foreground/50 opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"><X className="h-4 w-4" /></button>
            )}
          </div>
        ))}
      </div>

      {canEdit && (
        <div className="flex gap-2 pt-1">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder="Add a subtask" className="h-9" data-testid={`${testid}-input`} />
          <Button size="sm" variant="outline" onClick={add} data-testid={`${testid}-add`}><Plus className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}
